import { fixedUi } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sheinPlatformAPI } from '@/shein-platform/api/sheinPlatformApi';
import {
  activityTypeLabel,
  adjustFormLabel,
  formatMsTime,
  formatTimeRange,
  partakeFieldLabels,
  partakeScopeLabel,
} from '@/shein-platform/utils/sheinMarketingDisplay';
import '@/styles/shein-platform/shein-platform.css';
import '@/styles/shein-platform/shein-product-ops.css';

function RequirementBlock({ title, items }) {
  if (!Array.isArray(items) || !items.length) {
    return (
      <section className="spo-marketing-drawer__section">
        <h3>{title}</h3>
        <p className="spo-marketing-drawer__muted">{fixedUi("暂无数据")}</p>
      </section>
    );
  }
  return (
    <section className="spo-marketing-drawer__section">
      <h3>{title}</h3>
      <ul className="spo-marketing-drawer__req-list">
        {items.map((item, idx) => {
          const ok = Number(item?.isMeetRequirement) === 1;
          return (
            <li key={`${item?.requirementKey || idx}-${idx}`}>
              <strong>{item?.requirementKey || `规则 ${idx + 1}`}</strong>
              <span className={`spo-marketing-drawer__req-status spo-marketing-drawer__req-status--${ok ? 'pass' : 'fail'}`}>
                {ok ? '符合' : '不符合'}
              </span>
              {item?.requirementDesc ? (
                <span>{item.requirementDesc}</span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * SHEIN 营销活动详情抽屉（只读）
 */
export default function SheinMarketingActivityDrawer({
  open,
  shopId,
  activity,
  onClose,
}) {
  const { t } = useTranslation('workbench');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);

  const activityId = activity?.activityId;

  const loadDetail = useCallback(async () => {
    if (!shopId || activityId == null || activityId === '') return;
    setLoading(true);
    setError('');
    try {
      const res = await sheinPlatformAPI.getMarketingActivityDetail(shopId, activityId, {});
      const payload = res?.info && typeof res.info === 'object' ? res.info : res;
      setDetail(payload && typeof payload === 'object' ? payload : null);
    } catch (e) {
      setDetail(null);
      setError(e?.message || '加载活动详情失败');
    } finally {
      setLoading(false);
    }
  }, [shopId, activityId]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !activity) return;
    setDetail(null);
    setError('');
    loadDetail();
  }, [open, activity, loadDetail]);

  if (!open) return null;

  const data = detail || activity || {};
  const fieldLabels = partakeFieldLabels(data.partakeFieldList);
  const siteList = Array.isArray(data.siteList) ? data.siteList : [];
  const goodsSets = Array.isArray(data.goodsRequirementSetList) ? data.goodsRequirementSetList : [];
  const goodsReqs = goodsSets.flatMap((set) => (
    Array.isArray(set?.requirementList) ? set.requirementList : []
  ));

  return (
    <div className="shein-drawer-backdrop" role="presentation" onClick={onClose}>
      <aside
        className="shein-drawer shein-drawer--wide"
        role="dialog"
        aria-labelledby="spo-marketing-drawer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="shein-drawer__header">
          <div>
            <h2 id="spo-marketing-drawer-title">{data.activityName || activity?.activityName || t('marketing.enDetail')}</h2>
            <p className="shein-drawer__subtitle">
              {activityTypeLabel(data.activityType ?? activity?.activityType)}
              {activityId != null ? ` · ID ${activityId}` : ''}
            </p>
          </div>
          <button type="button" className="shein-drawer__close" onClick={onClose} aria-label={t('moduleShell.close')}>
            ×
          </button>
        </header>

        <div className="shein-drawer__body">
          {loading ? <p className="shein-drawer__hint">{fixedUi("正在加载详情…")}</p> : null}
          {error ? <p className="shein-drawer__error">{error}</p> : null}

          <div className="shein-detail-grid">
            <div className="shein-detail-row">
              <span className="shein-detail-row__label">{fixedUi("报名时间")}</span>
              <span className="shein-detail-row__value">
                {formatTimeRange(data.partakeStartTime, data.partakeEndTime)}
              </span>
            </div>
            <div className="shein-detail-row">
              <span className="shein-detail-row__label">{fixedUi("活动时间")}</span>
              <span className="shein-detail-row__value">
                {formatTimeRange(data.activityStartTime, data.activityEndTime)}
              </span>
            </div>
            <div className="shein-detail-row">
              <span className="shein-detail-row__label">{fixedUi("是否可报")}</span>
              <span className="shein-detail-row__value">
                {Number(data.canPartake) === 1
                  ? '可报名'
                  : (data.cannotPartakeReason || '不可报名')}
              </span>
            </div>
            <div className="shein-detail-row">
              <span className="shein-detail-row__label">{fixedUi("提报维度")}</span>
              <span className="shein-detail-row__value">{partakeScopeLabel(data.partakeScope)}</span>
            </div>
            <div className="shein-detail-row">
              <span className="shein-detail-row__label">{fixedUi("报价形式")}</span>
              <span className="shein-detail-row__value">{adjustFormLabel(data.adjustForm)}</span>
            </div>
            <div className="shein-detail-row">
              <span className="shein-detail-row__label">{fixedUi("填报项")}</span>
              <span className="shein-detail-row__value">
                {fieldLabels.length ? fieldLabels.join('、') : '—'}
              </span>
            </div>
            <div className="shein-detail-row">
              <span className="shein-detail-row__label">{fixedUi("可选站点")}</span>
              <span className="shein-detail-row__value">
                {siteList.length ? siteList.join('、') : '—'}
                {Number(data.supportSelectSitePartake) === 1 ? '（支持部分站点）' : ''}
              </span>
            </div>
            {data.supplyPriceCurrency ? (
              <div className="shein-detail-row">
                <span className="shein-detail-row__label">{fixedUi("供货价币种")}</span>
                <span className="shein-detail-row__value">{data.supplyPriceCurrency}</span>
              </div>
            ) : null}
            {data.lockInfo ? (
              <div className="shein-detail-row">
                <span className="shein-detail-row__label">{fixedUi("锁价说明")}</span>
                <span className="shein-detail-row__value">{data.lockInfo}</span>
              </div>
            ) : null}
            {data.maxPartakeGoodsNum != null ? (
              <div className="shein-detail-row">
                <span className="shein-detail-row__label">{fixedUi("最大提报数")}</span>
                <span className="shein-detail-row__value">{String(data.maxPartakeGoodsNum)}</span>
              </div>
            ) : null}
            {data.dependOnActivityId != null && data.dependOnActivityId !== '' ? (
              <div className="shein-detail-row">
                <span className="shein-detail-row__label">{fixedUi("关联活动")}</span>
                <span className="shein-detail-row__value">{String(data.dependOnActivityId)}</span>
              </div>
            ) : null}
            <div className="shein-detail-row">
              <span className="shein-detail-row__label">{fixedUi("报名截止")}</span>
              <span className="shein-detail-row__value">{formatMsTime(data.partakeEndTime)}</span>
            </div>
          </div>

          <RequirementBlock title={fixedUi("店铺要求")} items={data.storeRequirementList} />
          <RequirementBlock title={fixedUi("商品要求")} items={goodsReqs} />

          {data.activityIntroduction ? (
            <section className="spo-marketing-drawer__section">
              <h3>{fixedUi("活动介绍")}</h3>
              <div
                className="spo-marketing-drawer__html"
                // 平台 HTML 介绍，只读展示
                dangerouslySetInnerHTML={{ __html: String(data.activityIntroduction) }}
              />
            </section>
          ) : null}

          {data.benefitImage ? (
            <section className="spo-marketing-drawer__section">
              <h3>{fixedUi("权益说明")}</h3>
              <img
                className="spo-marketing-drawer__benefit"
                src={String(data.benefitImage)}
                alt={fixedUi("权益说明")}
              />
            </section>
          ) : null}
        </div>
      </aside>
    </div>
  );
}
